/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { Grid, Stack } from "@mui/material";
import { Method } from "axios";
import { H4 } from "components/generals/_Titles";
import FormikForm from "components/layout/FormikForm";
import BadgeIcon from "@mui/icons-material/Badge";
import { CoreAPI_URLs } from "constants/http.const";
import { Form, FormikProps } from "formik";
import useHttp from "hooks/http.hook";
import {
  CaseInterface,
  HeaderEnum,
  InsuranceInterface,
  PersonInterface,
  ProviderInterface,
  QueryResultInterface,
} from "medicalsystem-shared";
import { useTranslation } from "react-i18next";
import { FormProps } from "interfaces";
import {
  AutoCompleteOption,
  EntityPermissions,
  FormActionType,
  PermissionsUser,
  SelectArrayType,
} from "types";
import FormField from "components/generals/FormInput";

import Button from "components/generals/Button";
import FormActionButtons from "./custom/FormActionButtons";
import { caseFormYUPValidation, getSelectOptions } from "utils/form.utils";
import PermissionWrapper from "components/generals/PermissionWrapper";
import FormSelectInput from "components/generals/FormSelectInput";
import { useEffect, useMemo, useState } from "react";
import { StatusCaseEnum, TypeCaseEnum } from "medicalsystem-shared/dist/enums/case.enum";
import Autocomplete from "components/generals/Autocomplete";
import CaseAppointmentTable from "components/tables/CaseAppointmentTable";

/**
 * Form component for creating and managing medical cases.
 * Handles case creation with patient, doctor, insurance, and diagnosis information.
 * Includes case appointments table and supports case type and status management.
 * @param {FormProps<CaseInterface>} props - Component props including model, action, callbacks, and lists
 * @returns {React.ReactElement} The case form component
 */
const CaseForm: React.FC<FormProps<CaseInterface>> = ({
  model,
  action,
  onCloseAction,
  setModelList,
  modelList,
}) => {
  const [isLoading, _error, sendRequest] = useHttp();
  const { t } = useTranslation();
  const [doctorList, setDoctorList] = useState<ProviderInterface[]>([]);

  useEffect(() => {
    sendRequest(CoreAPI_URLs.DOCTOR_GET_ALL, onGetDoctorsHandler, "GET");
  }, []);

  const SELECT_OPTION = useMemo<AutoCompleteOption>(() => {
    return { label: "[SELECT]", ID: "" };
  }, []);

  const getFullName = (person: Partial<PersonInterface>, id: string) => {
    let fullName: AutoCompleteOption;
    if (id && person) {
      fullName = {
        label: person.name + " " + person.lastname,
        id: id,
      };
    } else {
      fullName = SELECT_OPTION;
    }
    return fullName;
  };

  const doctorOptions = useMemo<AutoCompleteOption[]>(() => {
    const result: AutoCompleteOption[] = [];
    doctorList.forEach((d) => {
      if (d.person) {
        result.push(getFullName(d.person, d.id));
      }
    });
    return result;
  }, [doctorList]);

  const onGetDoctorsHandler = (data: QueryResultInterface<ProviderInterface>) => {
    const sortedDoctors = data.results.sort((a, b) => a.person.name.localeCompare(b.person.name));
    setDoctorList(sortedDoctors);
  };

  const submitHandler = (model: Partial<InsuranceInterface>) => {
    const method: Method = action === FormActionType.NEW ? "POST" : "PUT";
    sendRequest(
      CoreAPI_URLs.MEDICAL_CASE_GENERAL_OPERATIONS,
      onCaseUpdateHandler,
      method,
      JSON.stringify(model),
      [{ header: HeaderEnum.RESOURCE_ID, value: model.id }],
      { successMessage: t("caseForm.medicalCaseUpdate") }
    );
  };

  const typeCaseOptions = useMemo<SelectArrayType[]>(() => getSelectOptions(TypeCaseEnum, t), []);

  const statusCaseOptions = useMemo<SelectArrayType[]>(
    () => getSelectOptions(StatusCaseEnum, t),
    []
  );

  const onCaseUpdateHandler = (medicalCase: CaseInterface) => {
    if (action === FormActionType.MODIFY) {
      setModelList(modelList.map((model) => (model.id === medicalCase.id ? medicalCase : model)));
    } else {
      setModelList((oldArray) => [...oldArray, medicalCase]);
    }

    onCloseAction();
  };

  return (
    <FormikForm
      onSubmit={(model) => {
        submitHandler(model);
      }}
      initialValues={model}
      validationSchema={caseFormYUPValidation(t)}
    >
      {({ values, errors, touched, setFieldValue, setFieldTouched, handleSubmit }: FormikProps<Partial<CaseInterface>>) => (
        <Form id="caseForm" autoComplete="true">
          <Stack alignItems="center" direction="row" gap={2} mb={3}>
            <BadgeIcon fontSize="large" />
            <H4>{t("caseForm.title")}</H4>
          </Stack>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormField
                type="text"
                label={t("caseForm.label")}
                name="label"
                value={values.label || ""}
                error={errors.label && touched.label}
              />
            </Grid>

            <Grid item xs={12} md={6} lg={6}>
              <FormSelectInput
                name="typeCase"
                label={t("caseForm.typeCase")}
                selectArray={[...typeCaseOptions]}
                defaultValue={values.typeCase ?? null}
              />
            </Grid>

            <Grid item xs={12} md={6} lg={6}>
              <FormSelectInput
                name="status"
                label={t("caseForm.status")}
                selectArray={[...statusCaseOptions]}
                defaultValue={values.status ?? null}
              />
            </Grid>

            <Grid item xs={12} md={6} id="cy-provideridFormContainer">
              <Autocomplete
                options={doctorOptions}
                loading={isLoading}
                name="provider.id"
                label={t("caseForm.manager")}
                error={touched.provider?.id && Boolean(errors.provider?.id)}
                onChange={(_e, value) => {
                  setFieldValue("provider.id", value?.id || '');
                  setFieldTouched("provider.id", true, false);
                }}
                defaultValue={
                  model?.provider?.id
                    ? {
                        id: model.provider.id,
                        label: model.provider.person.name + " " + model.provider.person.lastname,
                      }
                    : null
                }
                id="cy-providerid"
                noOptionsText={t("caseForm.noOptions")}
              />
              {errors.provider?.id&&(<div style={{fontSize: '10px', color: 'red'}}>
                {t("formUtils.doctorRequired")}
              </div>)}
              
            </Grid>

            <Grid item xs={12} md={6}>
              <FormField
                readOnly={true}
                type="text"
                label={t("caseForm.patient")}
                name="patient"
                value={values.patient.person.name || ""}
              />
            </Grid>

            <Grid item xs={12}>
              <FormField
                type="textarea"
                label={t("caseForm.comment")}
                name="comment"
                id="comment"
                multiline
                rows={10}
                value={values.comment || ""}
                error={errors.comment && touched.comment}
              />
            </Grid>
            <Grid item xs={12}>
              {model?.id && <CaseAppointmentTable caseSelected={model} />}
            </Grid>
          </Grid>

          <PermissionWrapper
            requiredPermissions={[
              action === FormActionType.NEW
                ? PermissionsUser[EntityPermissions.INSURANCE].CREATE
                : PermissionsUser[EntityPermissions.INSURANCE].EDIT,
            ]}
          >
            <Button id="cy-createMedicalCase" type="submit" variant="contained" color="info" disabled={isLoading}>
              {t(
                action === FormActionType.NEW
                  ? "stepperFormActionButtons.submit"
                  : "stepperFormActionButtons.update"
              )}
            </Button>
          </PermissionWrapper>

          <FormActionButtons showSubmitButton={false} closeAction={onCloseAction} />
        </Form>
      )}
    </FormikForm>
  );
};

export default CaseForm;
